import * as Yup from "yup";

export const addAdvancePaymentValidationSchema = Yup.object().shape({

  // 🔹 Employee
  employeeCode: Yup.number()
    .typeError("Select Employee")
    .required("Select Employee"),

  // 🔹 Type (NEW)
  type: Yup.string()
    .oneOf(["ADVANCE", "LOAN"])
    .required("Type is required"),

  // 🔹 Date
  advanceDate: Yup.date()
    .typeError("Enter valid date")
    .required("Advance date is required"),

  // 🔹 Amount
  advanceAmount: Yup.number()
    .typeError("Enter valid amount")
    .positive("Amount must be greater than 0")
    .required("Advance amount is required"),

  // 🔹 Reason
  reason: Yup.string().required("Reason is required"),

  // 🔷 Loan Specific Fields
  tenure: Yup.number().when("type", {
    is: "LOAN",
    then: (schema) =>
      schema
        .typeError("Enter valid tenure")
        .positive("Tenure must be greater than 0")
        .integer("Tenure must be integer")
        .required("Tenure is required"),
    otherwise: (schema) => schema.nullable(),
  }),

  interestRate: Yup.number().when("type", {
    is: "LOAN",
    then: (schema) =>
      schema
        .typeError("Enter valid interest rate")
        .min(0, "Interest cannot be negative")
        .max(100, "Interest too high")
        .required("Interest rate is required"),
    otherwise: (schema) => schema.nullable(),
  }),

  emi: Yup.number().when("type", {
    is: "LOAN",
    then: (schema) =>
      schema
        .typeError("Invalid EMI")
        .positive("EMI must be greater than 0")
        .required("EMI is required"),
    otherwise: (schema) => schema.nullable(),
  }),

  // 🔹 Repayment Dates
  repaymentStartDate: Yup.date()
    .typeError("Enter valid date")
    .when("type", {
      is: "LOAN",
      then: (schema) =>
        schema.required("Repayment start date is required"),
      otherwise: (schema) => schema.nullable(),
    }),

  repaymentEndDate: Yup.date()
    .typeError("Enter valid date")
    .when(["type", "repaymentStartDate"], {
      is: (type, startDate) => type === "LOAN" && !!startDate,
      then: (schema) =>
        schema
          .min(
            Yup.ref("repaymentStartDate"),
            "End date must be after start date"
          )
          .required("Repayment end date is required"),
      otherwise: (schema) => schema.nullable(),
    }),

  // 🔷 Approval Flow
  isApproved: Yup.boolean(),

  approvedBy: Yup.string().when("isApproved", {
    is: true,
    then: (schema) => schema.required("Approved By is required"),
    otherwise: (schema) => schema.nullable(),
  }),

  approvalDate: Yup.date()
  .nullable()
  .when("isApproved", {
    is: true,
    then: (schema) =>
      schema
        .typeError("Enter valid approval date")
        .required("Approval date is required")
        .min(
          Yup.ref("advanceDate"),
          "Approval date cannot be before advance date"
        ),
    otherwise: (schema) =>
      schema.nullable().notRequired(),
  }),

  // 🔷 Financial Tracking
  balanceAmount: Yup.number()
    .min(0, "Invalid balance")
    .nullable(),

  paidAmount: Yup.number()
    .min(0, "Invalid paid amount")
    .nullable(),

  // 🔷 Remarks
  remarks: Yup.string().max(250, "Max 250 characters"),

});

export const advanceRepaymentValidationSchema = Yup.object().shape({
  advancePaymentDetails: Yup.object().shape({
    slno: Yup.number().integer().required(),
    code: Yup.number().integer().required(),
    companyCode: Yup.number().integer().required(),
    employeeCode: Yup.number().integer().required(),
    installmentDate: Yup.string().typeError("Enter valid date").required(),
    installmentAmount: Yup.number().typeError("Enter valid amount").required(),
    balanceAmount: Yup.number().typeError("Enter valid amount").required(),
    paidAmount: Yup.number()
      .typeError("Enter valid amount")
      .required("Paid amount is required")
      .test(
        "paid-not-greater",
        "Paid amount cannot be greater than balance amount",
        function (value) {
          const { balanceAmount } = this.parent || {};
          if (value == null || balanceAmount == null) return true; // other validators handle required
          return value <= balanceAmount;
        }
      ),
    remarks: Yup.string(),
    createUser: Yup.string().required(),
    modifyUser: Yup.string(),
    modifyDate: Yup.string(),
    createDate: Yup.string().required(),
    showInPayslip: Yup.boolean().required(),
  }),
});

export const advancePaymentApproveValidationSchema = Yup.object().shape({
  isApproved: Yup.boolean().required("Please select approval status"),
  approvedBy: Yup.string().when("isApproved", {
    is: true,
    then: (schema) => schema.required("Approved By is required"),
    otherwise: (schema) => schema.notRequired(),
  }),
  approvalDate: Yup.string().when("isApproved", {
    is: true,
    then: (schema) => schema.required("Approval Date is required"),
    otherwise: (schema) => schema.notRequired(),
  }),
});
